import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X, Search, RotateCcw, AlertCircle, FileText } from 'lucide-react';
import { TextKeypad } from '@/components/ui/keypads/TextKeypad';
import { toast } from 'sonner';
import { salesVoidService, VoidFilters, SalesDocument } from './services/salesVoidService';

export const VoidSalesPage: React.FC = () => {
  const [filters, setFilters] = useState<VoidFilters>({
    numeroDocumento: '',
    dniRuc: ''
  });

  const [documents, setDocuments] = useState<SalesDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [isKeypadOpen, setIsKeypadOpen] = useState(false);
  const [activeField, setActiveField] = useState<'numeroDocumento' | 'dniRuc' | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<SalesDocument | null>(null);

  const handleSearch = async () => {
    if (!filters.numeroDocumento.trim() && !filters.dniRuc.trim()) {
      toast.error('Ingresa al menos un número de documento o DNI/RUC para buscar');
      return;
    }

    setLoading(true);
    try {
      const results = await salesVoidService.searchDocuments(filters);
      setDocuments(results);

      if (results.length === 0) {
        toast.info('No se encontraron documentos con los criterios de búsqueda');
      } else {
        toast.success(`Se encontraron ${results.length} documento(s)`);
      }

    } catch (error) {
      console.error('Error searching documents:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      toast.error(`Error al buscar documentos: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFilters({
      numeroDocumento: '',
      dniRuc: ''
    });
    setDocuments([]);
  };

  const handleKeypadOpen = (field: 'numeroDocumento' | 'dniRuc') => {
    setActiveField(field);
    setIsKeypadOpen(true);
  };

  const handleKeypadSubmit = (value: string) => {
    if (activeField) {
      setFilters(prev => ({
        ...prev,
        [activeField]: value
      }));
    }
  };

  const handleKeypadClose = () => {
    setIsKeypadOpen(false);
    setActiveField(null);
  };

  const handleVoidDocument = (document: SalesDocument) => {
    setSelectedDocument(document);
    setShowConfirmModal(true);
  };

  const confirmVoidDocument = async () => {
    if (!selectedDocument) return;

    try {
      const result = await salesVoidService.voidDocument(selectedDocument.id);
      toast.success(`Documento ${selectedDocument.numero} anulado exitosamente`);

      // Update document status in the list
      setDocuments(prev =>
        prev.map(doc =>
          doc.id === selectedDocument.id
            ? { ...doc, estado: 'ANULADO' as const }
            : doc
        )
      );

    } catch (error) {
      console.error('Error voiding document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      toast.error(`Error al anular el documento: ${errorMessage}`);
    } finally {
      setShowConfirmModal(false);
      setSelectedDocument(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-macartur-primary" />
            <div>
              <CardTitle>Anular Documentos</CardTitle>
              <CardDescription>
                Busca y anula boletas o facturas emitidas
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filtros de búsqueda */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Primera fila */}
            <div className="space-y-2">
              <Label htmlFor="numeroDocumento">Número de Documento</Label>
              <div className="relative">
                <Input
                  id="numeroDocumento"
                  value={filters.numeroDocumento}
                  onChange={(e) => setFilters(prev => ({ ...prev, numeroDocumento: e.target.value }))}
                  onClick={() => handleKeypadOpen('numeroDocumento')}
                  placeholder="Ej: B001-00000123"
                  className="pr-10 cursor-pointer"
                  readOnly
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dniRuc">DNI / RUC</Label>
              <div className="relative">
                <Input
                  id="dniRuc"
                  value={filters.dniRuc}
                  onChange={(e) => setFilters(prev => ({ ...prev, dniRuc: e.target.value }))}
                  onClick={() => handleKeypadOpen('dniRuc')}
                  placeholder="Documento del cliente"
                  className="pr-10 cursor-pointer"
                  readOnly
                />
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex space-x-2">
              <Button onClick={handleSearch} disabled={loading} className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>

            <div className="flex space-x-2">
              <Button variant="outline" onClick={handleClear} className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Limpiar
              </Button>
            </div>
          </div>

          {/* Resultados de búsqueda */}
          {documents.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Documentos encontrados ({documents.length})</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {documents.map((document) => (
                  <div
                    key={document.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-lg">{document.numero}</span>
                        <Badge
                          variant={document.estado === 'ANULADO' ? 'destructive' : 'default'}
                          className={document.estado === 'EMITIDO' ? 'bg-green-500 text-white hover:bg-green-600' : ''}
                        >
                          {document.estado}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Cliente:</span> {document.cliente.nombre}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Documento:</span> {document.cliente.documento}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Fecha:</span> {new Date(document.fechaEmision).toLocaleDateString('es-PE')}
                        </p>
                        <p className="text-sm font-semibold text-macartur-primary">
                          Total: S/ {document.total.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 ml-4">
                      {document.estado === 'EMITIDO' ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleVoidDocument(document)}
                          className="flex items-center gap-1"
                        >
                          <X className="w-4 h-4" />
                          Anular
                        </Button>
                      ) : (
                        <div className="text-sm text-red-600 font-medium px-3 py-1.5 bg-red-50 rounded-md">
                          Ya anulado
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Estado vacío */}
          {documents.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No hay documentos para mostrar</p>
              <p className="text-sm">Utiliza los filtros de búsqueda para encontrar documentos</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Text Keypad */}
      <TextKeypad
        isOpen={isKeypadOpen}
        onClose={handleKeypadClose}
        onSubmit={handleKeypadSubmit}
        initialValue={activeField ? filters[activeField] : ''}
        title={activeField === 'numeroDocumento' ? 'Número de Documento' : 'DNI / RUC'}
      />

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={() => setShowConfirmModal(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Confirmar Anulación
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas anular el siguiente documento?
            </DialogDescription>
          </DialogHeader>

          {selectedDocument && (
            <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
              <p><strong>Documento:</strong> {selectedDocument.numero}</p>
              <p><strong>Cliente:</strong> {selectedDocument.cliente.nombre}</p>
              <p><strong>Total:</strong> S/ {selectedDocument.total.toFixed(2)}</p>
              <p><strong>Fecha:</strong> {new Date(selectedDocument.fechaEmision).toLocaleDateString('es-PE')}</p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmVoidDocument}>
              Sí, Anular Documento
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
